import { useEffect, useRef, useState } from 'react';

function prefersReducedMotion(): boolean {
  try {
    return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
  } catch {
    return false;
  }
}

/**
 * Gibt den anzuzeigenden Wert zurück. Beim ersten Wechsel von ready=false auf true
 * wird einmalig von 0 auf target hochgezählt (ease-out). Danach springen Änderungen
 * von target sofort. Bei prefers-reduced-motion wird nie animiert.
 */
export function useCountUp(target: number, ready: boolean, durationMs = 700): number {
  const [value, setValue] = useState(target);
  const hasAnimatedRef = useRef(false);

  useEffect(() => {
    if (!ready) {
      setValue(target);
      return;
    }
    if (hasAnimatedRef.current) {
      setValue(target);
      return;
    }
    hasAnimatedRef.current = true;
    if (prefersReducedMotion()) {
      setValue(target);
      return;
    }

    let cancelled = false;
    let frameId = 0;
    const start = performance.now();
    setValue(0);

    const tick = (now: number) => {
      if (cancelled) return;
      const progress = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(progress >= 1 ? target : Math.round(target * eased));
      if (progress < 1) frameId = requestAnimationFrame(tick);
    };
    frameId = requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      cancelAnimationFrame(frameId);
    };
  }, [target, ready, durationMs]);

  return value;
}
